import { gql } from 'graphql-request';

import type { RequestDocument } from 'graphql-request';

// Selection for a `translation_memory_collection` record. `createdByUser` is the
// Dato `person` ref stamped on create (E8/OQ3); `name`/`description` are the flat
// collection's editable fields. api_keys are a hard contract with the PLAT-10837
// migration — GraphQL reads are camelCase, CMA writes snake_case.
export const TRANSLATION_MEMORY_COLLECTION_FRAGMENT = gql`
  {
    id
    name
    description
    createdByUser {
      id
      name
    }
  }
`;

export const TRANSLATION_MEMORY_COLLECTIONS_QUERY: RequestDocument = gql`
  query getTranslationMemoryCollections($showcaseId: ItemId) {
    allTranslationMemoryCollections(
      first: 100
      filter: { showcase: { eq: $showcaseId } }
      orderBy: name_ASC
    ) ${TRANSLATION_MEMORY_COLLECTION_FRAGMENT}
  }
`;

// Selection for a `translation_memory_entry` record (PLAT-10838). Languages are
// relational (`sourceLanguage` / `translationLanguage` link the shared `language`
// model — an empty `translationLanguage` ⇒ all target languages, E4); `collection`
// is an optional single link (empty ⇒ Default/uncategorized, E12); `_createdAt` /
// `_updatedAt` come from Dato record meta (no fields). api_keys are a hard contract
// with the migration — GraphQL reads camelCase, CMA writes snake_case.
export const TRANSLATION_MEMORY_ENTRY_FRAGMENT = gql`
  {
    id
    sourceTerm
    translation
    comment
    origin
    _createdAt
    _updatedAt
    collection {
      id
    }
    sourceLanguage {
      id
      caption
      value
    }
    translationLanguage {
      id
      caption
      value
    }
    createdByUser {
      id
      name
    }
    lastUpdatedByUser {
      id
      name
    }
  }
`;

// Paged: DatoCMS caps `first` at 100 per request, so the repository loops with
// `skip` until a short page (mirrors the backend loader). A single `first: 500`
// silently loaded only the first page — entries past it (e.g. terms late in the
// `sourceTerm_ASC` order) never reached the client, so their per-fragment TM
// provenance couldn't resolve and the Review panel showed the generic
// "N translation-memory terms" fallback (PLAT-10850).
export const TRANSLATION_MEMORY_ENTRIES_QUERY: RequestDocument = gql`
  query getTranslationMemoryEntries($showcaseId: ItemId, $skip: IntType) {
    allTranslationMemoryEntries(
      first: 100
      skip: $skip
      filter: { showcase: { eq: $showcaseId } }
      orderBy: sourceTerm_ASC
    ) ${TRANSLATION_MEMORY_ENTRY_FRAGMENT}
  }
`;

// Selection for a `translation_memory_import` record (PLAT-10839) — a retained
// bulk upload kept for provenance and the import-history list (E11). `file` is a
// single asset; `collections` is the multi-link import target (entries fan out
// one per collection, E12); `counts` is the { created, overwritten, skipped }
// result summary. Languages are relational links to the shared `language` model
// (empty `translationLanguage` ⇒ the file targeted all languages). api_keys are
// a hard contract with the migration — GraphQL reads camelCase, CMA writes
// snake_case.
export const TRANSLATION_MEMORY_IMPORT_FRAGMENT = gql`
  {
    id
    fileName
    file {
      url
    }
    sourceLanguage {
      id
      caption
      value
    }
    translationLanguage {
      id
      caption
      value
    }
    collections {
      id
      name
    }
    counts
    createdByUser {
      id
      name
    }
    _createdAt
  }
`;

export const TRANSLATION_MEMORY_IMPORTS_QUERY: RequestDocument = gql`
  query getTranslationMemoryImports($showcaseId: ItemId) {
    allTranslationMemoryImports(
      first: 100
      filter: { showcase: { eq: $showcaseId } }
      orderBy: _createdAt_DESC
    ) ${TRANSLATION_MEMORY_IMPORT_FRAGMENT}
  }
`;
